import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { InvoiceView } from "@/components/invoicing/InvoiceView";
import { getAllInvoices, getInvoiceById } from "@/lib/invoicing/invoice-service";
import type { Invoice } from "@/lib/invoicing/types";
import { InvoiceStatus } from "@/lib/invoicing/types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("fr-SN", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

const STATUS_STYLES: Record<InvoiceStatus, string> = {
  [InvoiceStatus.DRAFT]: "bg-stone-100 text-stone-600",
  [InvoiceStatus.ISSUED]: "bg-amber-50 text-amber-700 border border-amber-200",
  [InvoiceStatus.PAID]: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  [InvoiceStatus.CANCELLED]: "bg-red-50 text-red-600 border border-red-200",
  [InvoiceStatus.REFUNDED]: "bg-violet-50 text-violet-600 border border-violet-200",
};

const PAGE_SIZE = 20;

// ---------------------------------------------------------------------------
// Filter tab
// ---------------------------------------------------------------------------

type StatusFilter = "all" | InvoiceStatus;

interface FilterTabProps {
  readonly label: string;
  readonly value: StatusFilter;
  readonly active: boolean;
  readonly count: number;
  readonly onClick: (v: StatusFilter) => void;
}

function FilterTab({ label, value, active, count, onClick }: FilterTabProps) {
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "bg-amber-800 text-white"
          : "bg-white text-stone-600 border border-stone-200 hover:border-stone-300 hover:text-stone-800"
      }`}
    >
      {label}
      <span
        className={`inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold ${
          active ? "bg-amber-700 text-white" : "bg-stone-100 text-stone-500"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function InvoiceManagement() {
  const { t } = useTranslation("invoicing");
  useAdminPageTitle("Factures");

  const [invoices, setInvoices] = useState<readonly Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    const loaded = getAllInvoices();
    setInvoices(loaded);
    setIsLoading(false);
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      const matchesStatus =
        statusFilter === "all" || inv.status === statusFilter;
      const q = search.trim().toLowerCase();
      const matchesSearch =
        q === "" ||
        inv.number.toLowerCase().includes(q) ||
        inv.customer.name.toLowerCase().includes(q) ||
        inv.customer.phone.toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [invoices, statusFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function countByStatus(s: InvoiceStatus): number {
    return invoices.filter((i) => i.status === s).length;
  }

  function handleView(invoiceId: string): void {
    const inv = getInvoiceById(invoiceId);
    if (inv) setSelectedInvoice(inv);
  }

  if (selectedInvoice) {
    return (
      <AdminLayout>
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setSelectedInvoice(null)}
            className="flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
            {t("admin.title")}
          </button>
          <InvoiceView invoice={selectedInvoice} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-1">
          <h1 className="font-display text-2xl font-semibold text-stone-900">
            {t("admin.title")}
          </h1>
          <p className="text-sm text-stone-500">
            {invoices.length} {t("admin.pagination_results")}
          </p>
        </div>

        {/* Filters row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Status tabs */}
          <div className="flex flex-wrap gap-2">
            <FilterTab
              label={t("admin.filter_all")}
              value="all"
              active={statusFilter === "all"}
              count={invoices.length}
              onClick={setStatusFilter}
            />
            <FilterTab
              label={t("admin.filter_issued")}
              value={InvoiceStatus.ISSUED}
              active={statusFilter === InvoiceStatus.ISSUED}
              count={countByStatus(InvoiceStatus.ISSUED)}
              onClick={setStatusFilter}
            />
            <FilterTab
              label={t("admin.filter_paid")}
              value={InvoiceStatus.PAID}
              active={statusFilter === InvoiceStatus.PAID}
              count={countByStatus(InvoiceStatus.PAID)}
              onClick={setStatusFilter}
            />
            <FilterTab
              label={t("admin.filter_cancelled")}
              value={InvoiceStatus.CANCELLED}
              active={statusFilter === InvoiceStatus.CANCELLED}
              count={countByStatus(InvoiceStatus.CANCELLED)}
              onClick={setStatusFilter}
            />
          </div>

          {/* Search */}
          <div className="relative">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("admin.search_placeholder")}
              className="h-9 w-full rounded-lg border border-stone-200 bg-white pl-9 pr-4 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-600/30 sm:w-72"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          {isLoading ? (
            <div className="space-y-px p-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-lg bg-stone-100" />
              ))}
            </div>
          ) : paginated.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <p className="text-sm font-medium text-stone-700">
                {t("admin.no_results")}
              </p>
              <p className="mt-1 max-w-[280px] text-xs text-stone-400">
                {t("admin.no_results_description")}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50/80">
                  <th className="px-4 py-3 text-left font-semibold text-stone-500">
                    {t("admin.column_number")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-stone-500">
                    {t("admin.column_date")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-stone-500">
                    {t("admin.column_customer")}
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-stone-500">
                    {t("admin.column_total")}
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-stone-500">
                    {t("admin.column_status")}
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-stone-500">
                    <span className="sr-only">{t("admin.column_actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((inv) => {
                  const statusKey = inv.status.toLowerCase() as Lowercase<InvoiceStatus>;
                  return (
                    <tr
                      key={inv.id}
                      data-testid="invoice-row"
                      className="border-b border-stone-50 last:border-b-0 hover:bg-stone-50/50"
                    >
                      <td className="px-4 py-3 font-medium text-stone-800">
                        {inv.number}
                      </td>
                      <td className="px-4 py-3 text-stone-600">
                        {formatDate(inv.date)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-stone-800">
                          {inv.customer.name}
                        </p>
                        <p className="text-xs text-stone-500">
                          {inv.customer.phone}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-stone-800">
                        {formatCurrency(inv.total)} FCFA
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[inv.status]}`}
                        >
                          {t(`status.${statusKey}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          data-testid="view-invoice"
                          onClick={() => handleView(inv.id)}
                          className="text-sm font-medium text-amber-700 hover:text-amber-900 transition-colors"
                        >
                          {t("actions.view")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span>
              {(page - 1) * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE, filtered.length)}{" "}
              {t("admin.pagination_of")} {filtered.length}{" "}
              {t("admin.pagination_results")}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-600 hover:border-stone-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Page précédente"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-600 hover:border-stone-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Page suivante"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
